import React, { useState } from "react";
import dayjs from "dayjs";

import Button from "components/Button";
import Collapsible from "components/Collapsible";
import RichTextEditor from "components/base/RichTextEditor";
import SvgButton from "components/base/SvgButton";
import SvgIcon from "components/base/SvgIcon";
import { cn } from "utils";
import { getPurifiedParsedMarkDown, purifyMarkDown } from "utils/markdown";
import { validateNote } from "utils/validation";

const notesTitleClassName =
  "font-inter text-base font-medium normal-case text-slate-700";

const prepareDate = (createdAt) => {
  if (!createdAt) return "";

  const createdDaysAgo = dayjs().diff(createdAt, "days");
  const dateFormat = createdDaysAgo >= 2 ? "MMM DD YYYY" : "MMM DD YYYY, hh:mm";

  return dayjs(createdAt).format(dateFormat);
};

const NotesPanelCard = ({ note, canDelete, onDelete }) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = () => {
    const confirmDeleting = window.confirm(
      "Are you sure you want to delete the note?"
    );

    if (confirmDeleting) {
      setIsDeleting(true);
      onDelete(note.id);
    }
  };

  return (
    <div className="rounded-md bg-surface p-4 mb-4">
      <div className="grid grid-cols-1 gap-1 md:grid-cols-[1fr_auto] md:items-center md:gap-4">
        <p className="flex items-center gap-1.5 font-inter text-xs font-medium text-slate-700">
          <SvgIcon
            type="noteList"
            className="h-3 w-3 shrink-0 text-slate-700"
          />
          {note.creator || "user"}
        </p>
        <span className="font-inter text-xs font-normal text-gray-500 md:justify-self-end">
          {prepareDate(note.createdAt)}
        </span>
      </div>

      <div className="mt-3 flex items-start justify-between gap-2">
        <div
          className="prose prose-sm max-w-none font-inter text-sm text-slate-700"
          dangerouslySetInnerHTML={{
            __html: getPurifiedParsedMarkDown(note.content),
          }}
        />
        {canDelete && (
          <SvgButton
            icon="cross"
            iconProps={{ fill: "tomato", title: "Delete this note?" }}
            onClick={handleDeleteClick}
            isLoading={isDeleting}
          />
        )}
      </div>

      {!note.isPublic && (
        <div className="mt-2 flex justify-end">
          <span className="font-inter text-xs font-normal text-gray-500">
            Private
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * Shared notes UI (list + editor). Distinct from `components/Notes` (single note card).
 */
const NotesPanel = ({
  notes = [],
  loggedInUser,
  notesCount,
  label = "Notes",
  onSubmit,
  onDelete,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [note, setNote] = useState({
    content: "",
    isPublic: false,
    successMessage: "",
    errorMessage: "",
  });

  const sortedNotes = [...(notes || [])].sort((a, b) =>
    dayjs(a.createdAt).isAfter(dayjs(b.createdAt)) ? -1 : 1
  );
  const hasNotes = sortedNotes.length > 0;
  const preferredUsername = loggedInUser?.attributes?.preferred_username;

  const handleChange = (value, valueKey) => {
    setNote((prev) => ({
      ...prev,
      [valueKey]: value,
      errorMessage: "",
      successMessage: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submitting) {
      return;
    }

    setSubmitting(true);

    validateNote({
      data: note,
      onSuccess: async (validNote) => {
        validNote.content = purifyMarkDown(validNote.content);
        const res = await onSubmit(validNote);

        if (res.isSuccess) {
          setNote((prev) => ({
            ...prev,
            successMessage: "Note successfully added",
            errorMessage: "",
            content: "",
          }));
          setExpanded(true);
        } else {
          setNote((prev) => ({
            ...prev,
            errorMessage: "Something went wrong",
            successMessage: "",
          }));
        }

        setSubmitting(false);
      },
      onError: (errors) => {
        setNote((prev) => ({
          ...prev,
          errorMessage: Object.values(errors)[0],
        }));
        setSubmitting(false);
      },
    });
  };

  return (
    <div className="flex flex-col">
      <form onSubmit={handleSubmit}>
        {hasNotes ? (
          <Collapsible
            label={`${label}${notesCount ? ` (${notesCount})` : ""}`}
            labelClassName={notesTitleClassName}
            isVisible={expanded}
          >
            <div className="flex flex-col gap-3">
              {sortedNotes.map((item) => (
                <NotesPanelCard
                  key={item.id || item.createdAt}
                  note={item}
                  canDelete={preferredUsername === item.creator}
                  onDelete={onDelete}
                />
              ))}
            </div>
          </Collapsible>
        ) : (
          <div className="mb-4">
            <p className={notesTitleClassName}>{label}</p>
            <p className="mt-4 font-inter text-xs font-normal text-gray-500">
              No notes to display
            </p>
          </div>
        )}

        <RichTextEditor
          value={note.content}
          valueKey="content"
          onChange={handleChange}
        />

        <div className="flex flex-col items-start">
          {note.successMessage && (
            <span className="mt-1 text-sm font-bold text-green-600">
              {note.successMessage}
            </span>
          )}

          {note.errorMessage && (
            <span className="mt-1 text-sm font-bold text-red-600">
              {note.errorMessage}
            </span>
          )}

          <Button
            type="submit"
            className={cn(
              "mt-4 rounded-md border border-solid border-slate-300 bg-white px-4 py-2 font-inter text-sm font-medium normal-case text-slate-700 shadow-none hover:text-slate-700 hover:shadow-none",
              {
                "animate-pulse cursor-not-allowed": submitting,
              }
            )}
          >
            Save note
          </Button>
        </div>
      </form>
    </div>
  );
};

export default NotesPanel;
